import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Events from "./pages/Events";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import RegisterUser from "./pages/RegisterUser";
import AdminManagement from "./pages/AdminManagement";
import EventsSuperAdmin from "./pages/EventsSuperAdmin";
import MyEventsAdmin from "./pages/MyEventsAdmin";
import DevicesManagement from "./pages/DevicesManagement";
import SessionLogs from "./pages/SessionLogs";
import PublicEventRegister from "./pages/PublicEventRegister";
import DevicesByEvent from "./pages/DevicesByEvent";
import TicketTypesPage from "./pages/dashboard/TicketTypesPage";
// import PublicEventRegister from "./pages/PublicEventRegister";
import DeviceSessions from "./pages/DeviceSessions";
import EventSessionLogs from "./pages/EventSessionLogs";
import EventDashboard from "./pages/EventDashboard";
import EventDashboardLayout from "./pages/EventDashboardLayout";
import Overview from "./pages/dashboard/Overview";
import EntryExit from "./pages/dashboard/EntryExit";
import DashboardUsers from "./pages/dashboard/Users";
import Devices from "./pages/dashboard/Devices";
import PeakHours from "./pages/dashboard/PeakHours";
import { syncPendingOperations, refreshAllCachedData } from "./services/api";
import { Toaster } from "react-hot-toast";
import { SocketProvider } from "./context/SocketContext";
import UserManagement from "./pages/UserManagement";
import AssignEditors from "./pages/AssignEditors";
import AssignScanners from "./pages/AssignScanners";
import Scan from "./pages/Scan";
import MySessions from "./pages/MySessions";
import Profile from "./pages/Profile";
import CustomFieldsManager from "./pages/CustomFieldsManager";
import ScannerDashboard from "./pages/ScannerDashboard";
import AttendeeDashboard from "./pages/AttendeeDashboard";
import WalletLayout from "./pages/Wallet/WalletLayout";
import WalletOverview from "./pages/Wallet/WalletOverview";
import WalletRecharge from "./pages/Wallet/WalletRecharge";
import WalletTransactions from "./pages/Wallet/WalletTransactions";
import WalletVendors from "./pages/Wallet/WalletVendors";
import WalletReports from "./pages/Wallet/WalletReports";
import AttendeeWallet from "./pages/AttendeeWallet";
import VendorDashboard from "./pages/VendorDashboard";
import VendorOverview from "./pages/vendor/VendorOverview";
import VendorFood from "./pages/vendor/VendorFood";
import VendorTransactions from "./pages/vendor/VendorTransactions";
import VendorFoodList from "./pages/vendor/VendorFoodList";
import VendorOrders from "./pages/vendor/VendorOrders";
import CreateOrder from "./pages/vendor/CreateOrder";
import OrderQR from "./pages/vendor/OrderQR";
import PaymentPage from "./pages/vendor/PaymentPage";
import VendorBill from "./pages/vendor/VendorBill";
import { QueryProvider } from "./providers/QueryProvider";
import { ToastProvider } from "./hooks/useToast";
import MealsPage from "./pages/dashboard/MealsPage";
// import MealRedeemPage from "./pages/dashboard/MealRedeemPage";

function App() {
  return (
    <QueryProvider>
      <ToastProvider>
        <AuthProvider>
          <SocketProvider>
            <Router>
              <AppContent />
            </Router>
          </SocketProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryProvider>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const syncTimeoutRef = useRef(null);

  useEffect(() => {
    const handleOnline = async () => {
      console.log("Online – syncing pending operations");
      await syncPendingOperations();
    };
    const handleOffline = () => {
      console.log("Offline – using local cache");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    if (navigator.onLine && user && sessionStorage.getItem("token")) {
      // refreshAllCachedData();
    }
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      {user ? (
        <div className="h-screen bg-gray-100">
          <Navbar />
          <div className="pt-[86px] flex h-full">
            <Sidebar />
            <main className="flex-1 ml-64 overflow-y-auto p-6">
              <Routes>
                {/* Common routes for all authenticated users */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute
                      allowedRoles={["super_admin", "admin", "event_admin"]}
                    >
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sessions"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        // "event_editor",
                      ]}
                    >
                      <SessionLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-sessions"
                  element={
                    <ProtectedRoute allowedRoles={["device_scanner", "user"]}>
                      <MySessions />
                    </ProtectedRoute>
                  }
                />

                {/* Super Admin & Admin */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin", "admin",]}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                      <RegisterUser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events-manager"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                      <EventsSuperAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/devices"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "event_editor",
                      ]}
                    >
                      <DevicesManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:eventId/devices"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "device_scanner",
                        "event_editor",
                      ]}
                    >
                      <DevicesByEvent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/devices/:deviceId/sessions"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "device_scanner",
                      ]}
                    >
                      <DeviceSessions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:eventId/meals"
                  element={
                    <ProtectedRoute
                      allowedRoles={["super_admin", "admin", "event_admin", "event_editor"]}
                    >
                      <MealsPage />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="/events/:eventId/meals/redeem"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "event_editor",
                        "vendor",
                      ]}
                    >
                      <MealRedeemPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/meal-counter"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <MealRedeemPage />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path="/events/:eventId/users"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "device_scanner",
                        "event_editor",
                      ]}
                    >
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:eventId/ticket-types"
                  element={
                    <ProtectedRoute
                      allowedRoles={["super_admin", "admin", "event_admin", "event_editor"]}
                    >
                      <TicketTypesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:eventId/sessions"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "event_editor",
                      ]}
                    >
                      <EventSessionLogs />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/events/:eventId/dashboard"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "super_admin",
                        "admin",
                        "event_admin",
                        "event_editor",
                      ]}
                    >
                      <EventDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<Overview />} />
                  <Route path="entry-exit" element={<EntryExit />} />
                  <Route path="users" element={<DashboardUsers />} />

                  <Route path="devices" element={<Devices />} />
                  <Route path="peak-hours" element={<PeakHours />} />
                </Route>
                <Route
                  path="/custom-fields"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <CustomFieldsManager />
                    </ProtectedRoute>
                  }
                />

                {/* Routes for event_admin only */}
                <Route
                  path="/my-events"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "event_admin",
                        "event_editor",
                        "admin",
                        "device_scanner",
                      ]}
                    >
                      <MyEventsAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/assign-editors"
                  element={
                    <ProtectedRoute allowedRoles={["event_admin"]}>
                      <AssignEditors />
                    </ProtectedRoute>
                  }
                />

                {/* Routes for event_editor only */}
                <Route
                  path="/assign-scanners"
                  element={
                    <ProtectedRoute
                      allowedRoles={["event_admin", "event_editor"]}
                    >
                      <AssignScanners />
                    </ProtectedRoute>
                  }
                />

                {/* Routes for device_scanner only */}
                <Route
                  path="/scan"
                  element={
                    <ProtectedRoute allowedRoles={["device_scanner"]}>
                      <Scan />
                    </ProtectedRoute>
                  }
                />

                {/* Routes for regular user (attendee) */}
                <Route
                  path="/my-wallet"
                  element={
                    <ProtectedRoute allowedRoles={["user"]}>
                      <AttendeeWallet />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute allowedRoles={["user"]}>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/attendee-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={["user"]}>
                      <AttendeeDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy routes */}
                <Route
                  path="/admins"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <AdminManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <Events />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:eventId/dashboard-old"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <EventDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* ❌ REMOVED: Global Wallet routes */}
                {/* <Route path="/wallet/*" element={...} /> */}

                {/* ========== EVENT-BASED WALLET ROUTES ========== */}
                {/* ✅ Using your existing WalletLayout with event-based routes */}
                <Route
                  path="/events/:eventId/wallet/*"
                  element={
                    <ProtectedRoute
                      allowedRoles={["super_admin", "admin", "event_admin", "event_editor"]}
                    >
                      <WalletLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="recharge" replace />} />
                  <Route path="recharge" element={<WalletRecharge />} />
                  <Route path="transactions" element={<WalletTransactions />} />
                  <Route path="vendors" element={<WalletVendors />} />
                  <Route path="reports" element={<WalletReports />} />
                </Route>

                {/* Vendor routes */}
                <Route
                  path="/vendor-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor-transactions"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/overview"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/food"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorFoodList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/food/add"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorFood />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/food/edit/:id"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorFood />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/transactions"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor-dashboard"
                  element={<Navigate to="/vendor/overview" replace />}
                />
                <Route
                  path="/vendor/orders"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <VendorOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/orders/create"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <CreateOrder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/orders/qr/:id"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <OrderQR />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/payment/:id"
                  element={
                    <ProtectedRoute allowedRoles={["vendor"]}>
                      <PaymentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor/orders/:id/bill"
                  element={<VendorBill />}
                />

               <Route
  path="/"
  element={
    <Navigate
      to={
        user?.role === "user"
          ? "/attendee-dashboard"
          : user?.role === "event_editor" || user?.role === "device_scanner"
          ? "/my-events"
          : "/dashboard"
      }
      replace
    />
  }
/>

               <Route
  path="*"
  element={
    <Navigate
      to={
        user?.role === "user"
          ? "/attendee-dashboard"
          : user?.role === "event_editor" || user?.role === "device_scanner"
          ? "/my-events"
          : "/dashboard"
      }
      replace
    />
  }
/>
                <Route
                  path="/bill/:id"
                  element={
                    <ProtectedRoute>
                      <VendorBill />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </main>
          </div>
        </div>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterUser />} />

          <Route
            path="/event/:eventId/register"
            element={<PublicEventRegister />}
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </>
  );
}

export default App;
